import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Container,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client'; 
import { ReactComponent as TopLeftChartSVG } from './assets/svgs/top-left-chart.svg';
import { ReactComponent as LeftDesignSVG } from './assets/svgs/left-lines.svg';
import { ReactComponent as RightDesignSVG } from './assets/svgs/right-lines.svg';
import { ReactComponent as TopRightChartSVG } from './assets/svgs/top-right-chart.svg';


interface LoginPageProps {
  onLoginSuccess: () => void; 
}

// GraphQL query to check current user
const GET_CURRENT_USER_FOR_LOGIN_INVITE = gql`
  query GetCurrentUserForLoginInvite {
    me {
      id
      email
    }
  }
`;

const FinOpsLogo = () => {
  const iconSize = 35;
  const squareBackgroundColor = '#0064FF';
  const finOpsIconUrl = '/finops-icon.svg'; 
  const navigate = useNavigate();
  const handleBoxClick = () => {
  navigate('/');
};
  return (
    <Box onClick={handleBoxClick} sx={{ display: 'flex', alignItems: 'center', marginLeft: '25px', marginTop:'20px', Height:'35px', width :'144.83px', cursor: 'pointer'}}>
      <Box
        sx={{
          width: iconSize,
          height: iconSize,
          backgroundColor: squareBackgroundColor,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '10px',
        }}
      >
        <img
          src={process.env.PUBLIC_URL + finOpsIconUrl}
          alt="FinOps Icon"
          style={{ width: '30px', height: '35px', marginLeft: '15px' }}
        />
      </Box>
      <Typography
        variant="h5"
        component="div"
        sx={{
          color: '#0064FF', fontWeight: 1000, fontFamily: 'Gilroy-SemiBold',
          fontSize: '22px', lineHeight: '15px', letterSpacing: '1px', whiteSpace: 'nowrap',
        }}
      >
        Horizon
      </Typography>
    </Box>
  );
};

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme(); 
  const googleIconUrl = '/GoogleLogo.svg';
  const maskGroupUrl = '/Mask_group.svg';

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const { data: userData, loading: userLoading } = useQuery(GET_CURRENT_USER_FOR_LOGIN_INVITE, {
    fetchPolicy: 'network-only', 
  });

  // 1. Effect to capture token from URL on initial load
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    if (token) {
      sessionStorage.setItem('pendingInvitationToken', token);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);


  // 2. Effect to redirect if user is already authenticated
  useEffect(() => {
    if (userLoading) { // Still checking auth status
      return;
    }

    if (userData?.me) { 
      console.log("LoginPage: User is authenticated. Calling onLoginSuccess.");
      onLoginSuccess();
    }
  }, [
    userData,
    userLoading,
    onLoginSuccess,
  ]);

  const handleGoogleSsoLogin = () => {
    // Token is already in sessionStorage if it was in the URL.
    // Redirect to Google OAuth. After completion, user will be back,
    // GET_CURRENT_USER_FOR_LOGIN_INVITE will refetch, and the useEffect above will trigger onLoginSuccess.
    window.location.href = `${BACKEND_URL}/auth/google`;
  };

  const customGradient = "linear-gradient(150.56deg, rgba(217, 231, 253, 0.92) 4.39%, rgba(255, 255, 255, 0.952321) 29.93%, rgba(255, 255, 255, 0.96) 61.13%, rgba(217, 231, 253, 0.92) 122.4%)";
  const backgroundImage = 'url(' + process.env.PUBLIC_URL + '/images/background-image.png)';
  const showPrimaryLoadingScreen = userLoading; 

  return (
    <Box
      sx={{
        background: `${customGradient}, ${backgroundImage}`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Background SVGs */}
      <Box sx={{ position: 'absolute', top: '404px', left: '100px', width: '350px', height: '205px', zIndex: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><TopLeftChartSVG /></Box>
      <Box sx={{ position: 'absolute', top: '200px', left: '100px', width: '350px', height: '205px', zIndex: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LeftDesignSVG /></Box>
      <Box sx={{ position: 'absolute', top: '404px', right: '100px', width: '350px', height: '205px', zIndex: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RightDesignSVG /></Box>
      <Box sx={{ position: 'absolute', top: '200px', right: '100px', width: '350px', height: '205px', zIndex: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><TopRightChartSVG /></Box>
      <Box component="header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 8, py: 1.2 }}>
        <FinOpsLogo />
        <IconButton aria-label="close login page" sx={{ color: 'text.primary' }} onClick={() => navigate('/')}>
        </IconButton>
      </Box>

      <Container
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          py: { xs: 0, md: 0 },
        }}
      >
        {showPrimaryLoadingScreen ? (
           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: theme.palette.text.primary, backgroundColor: 'rgba(255,255,255,0.8)', padding: 3, borderRadius: 2 }}>
             <CircularProgress color="inherit" />
             <Typography sx={{ mt: 2 }}>Checking your session...</Typography>
           </Box>
         ) : (
         <Box
           sx={{
             p: { xs: 5, sm: 6 },
             display: 'flex',
             flexDirection: 'column',
             alignItems: 'center',
             borderRadius: '16px',
             width: '464px',
             height : 'auto',
             minHeight: '387px',
             backgroundColor: 'rgba(255, 255, 255, 0.95)',
             backdropFilter: 'blur(8px)',
             zIndex: 1,
             position: 'relative',
           }}
         >
           <Typography component="span" >
             <img
               src={process.env.PUBLIC_URL + maskGroupUrl}
               alt="Login Icon"
               style={{ width: '107px', height: '107px', marginTop:'2px'}}
             />
           </Typography>
           

           <Typography sx={{ fontSize: '30px', fontWeight: 700, mb: 1, fontFamily: 'Inter', lineHeight: '45px' ,  verticalAlign: 'middle',}}>
             Welcome!
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px',fontWeight: 400,lineHeight: '21px', mb: 3, textAlign: 'center', fontFamily: 'Inter' }}>
             Login made easy, just use your Google account to login and start exploring
           </Typography>

           <Stack direction="row" border={1} borderRadius='8px' borderColor='#F3F3F3' spacing={3} sx={{ mb: 2 }}>
             <IconButton
               aria-label="login with google"
               onClick={handleGoogleSsoLogin}
               sx={{ width: '177px',height:'36px', backgroundColor: ' #FAFAFA', borderRadius: '8px', p: 1.5 ,borderColor:'1px solid rgba(243, 243, 243, 1)'}}
             >
             <img
                 src={process.env.PUBLIC_URL + googleIconUrl}
                 alt="Google Logo"
                 style={{ width: '20px', height: '20px' }}
             />
             <Typography variant="body2" color="text.secondary" sx={{fontWeight:'500', fontSize: '14px', color:' #333333', textAlign: 'center', fontFamily: 'Inter', marginLeft:'8px' }}>
                 Login With Google
             </Typography>
             </IconButton>
           </Stack>
         </Box>
         )}
      </Container>

      <Box component="footer" sx={{ py: 4, px: 0, textAlign: 'center'}}>
        <Typography variant="caption" color="text.secondary" fontFamily="Inter" letterSpacing='1px' fontSize={12} fontWeight={400} >
          © {new Date().getFullYear()} Searce Co-sourcing Ltd. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
